import PropTypes from 'prop-types';

/**
 * Authoring UI for the attendance "same day" validation.
 *
 * Production shape (1 occurrence in admin_buildex_dev):
 *   {
 *     arrayField: string,
 *     rootField:  string,
 *     itemField:  string,
 *   }
 *
 * Semantics: every entry in `arrayField[*][itemField]` must fall on the
 * same calendar day as the root-level `rootField`.
 *
 * UX: a single row of three inputs plus a "Clear" button. Copy is
 * business-facing; the saved schema still uses the existing runtime keys.
 */
export default function SameDayForm({
	value,
	onChange,
	disabled = false,
	fieldOptions = [],
}) {
	const safe = value && typeof value === 'object' ? value : {};
	const isAuthored = !!(safe.arrayField || safe.rootField || safe.itemField);

	const update = (key, next) => {
		const merged = { ...safe, [key]: next };
		const nonEmpty = merged.arrayField || merged.rootField || merged.itemField;
		onChange(nonEmpty ? merged : null);
	};

	const clear = () => onChange(null);

	const fieldListId = 'cv-sd-field-options';

	return (
		<div className="dsv-cv-form" data-testid="cv-sd-form">
			<p className="dsv-cv-form__hint">
				Use this for attendance records. It checks that all attendance
				dates are on the same calendar day.
			</p>

			<datalist id={fieldListId}>
				{fieldOptions.map((opt) => <option key={opt} value={opt} />)}
			</datalist>

			<div className="dsv-modal__field-row">
				<div className="dsv-modal__field dsv-modal__field--third">
					<label className="dsv-modal__label">Attendance List Field</label>
					<input
						className="dsv-modal__input"
						type="text"
						list={fieldListId}
						value={safe.arrayField ?? ''}
						disabled={disabled}
						onChange={(e) => update('arrayField', e.target.value)}
						placeholder="e.g. person_attendance_details"
					/>
				</div>
				<div className="dsv-modal__field dsv-modal__field--third">
					<label className="dsv-modal__label">Date Field To Match</label>
					<input
						className="dsv-modal__input"
						type="text"
						list={fieldListId}
						value={safe.rootField ?? ''}
						disabled={disabled}
						onChange={(e) => update('rootField', e.target.value)}
						placeholder="e.g. person_attendance_date"
					/>
				</div>
				<div className="dsv-modal__field dsv-modal__field--third">
					<label className="dsv-modal__label">Time Field In Each Row</label>
					<input
						className="dsv-modal__input"
						type="text"
						list={fieldListId}
						value={safe.itemField ?? ''}
						disabled={disabled}
						onChange={(e) => update('itemField', e.target.value)}
						placeholder="e.g. time_in"
					/>
				</div>
			</div>

			{!disabled && isAuthored && (
				<button
					type="button"
					className="dsv-btn dsv-btn--secondary"
					onClick={clear}
					data-testid="cv-sd-clear"
				>
					Clear
				</button>
			)}
		</div>
	);
}

SameDayForm.propTypes = {
	value: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	fieldOptions: PropTypes.arrayOf(PropTypes.string),
};
